import { MaterialCommunityIcons } from '@expo/vector-icons';
import { FilterModel } from '@gripp/shared-logic';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, Pressable, StyleSheet, Text } from 'react-native';
import { Badge } from 'react-native-paper';
import { Colors } from '../../themes';
import { MaterialCommunityIconName, MaterialIconName } from '../../utils';

type BaseFilterButtonProps = {
  filter: FilterModel;
  onGoFilter: () => void;
  icon?: MaterialIconName | MaterialCommunityIconName;
  label?: string;
  isColumnsFilter?: boolean;
  isAccountTypesFilter?: boolean;
  isTypesFilter?: boolean;
  isLabelsFilter?: boolean;
  isDateFilter?: boolean;
  isCategoriesFilter?: boolean;
};

export const BaseFilterButton = ({
  filter,
  onGoFilter,
  label,
  isColumnsFilter,
  isAccountTypesFilter,
  isTypesFilter,
  isLabelsFilter,
  isDateFilter,
  isCategoriesFilter,
}: BaseFilterButtonProps) => {
  const { t } = useTranslation();
  const isWeb = Platform.OS === 'web';

  const [isHovered, setIsHovered] = useState(false);

  const hasGroups = () => {
    return filter.getGroupsCount && filter.getGroupsCount() > 0;
  };

  const hasAccountTypes = () => {
    return filter.getAccountTypesCount && filter.getAccountTypesCount() > 0;
  };

  const hasTypes = () => {
    return filter.getTypesCount && filter.getTypesCount() > 0;
  };

  const hasLabels = () => {
    return filter.getLabelsCount && filter.getLabelsCount() > 0;
  };

  const hasColumns = () => {
    return filter.getColumnsCount && filter.getColumnsCount() > 0;
  };

  const hasCategories = () => {
    return filter.getCategoriesCount && filter.getCategoriesCount() > 0;
  };

  const hasDate = () => {
    return filter.isDateFilterActive();
  };

  const isFilterActive =
    (hasGroups() &&
      !isColumnsFilter &&
      !isTypesFilter &&
      !isLabelsFilter &&
      !isAccountTypesFilter &&
      !isCategoriesFilter) ||
    (hasAccountTypes() && isAccountTypesFilter) ||
    (isColumnsFilter && hasColumns()) ||
    (isTypesFilter && hasTypes()) ||
    (isLabelsFilter && hasLabels()) ||
    (isCategoriesFilter && hasCategories()) ||
    (isDateFilter && hasDate());

  const getBackgroundColor = () => {
    return (
      (isFilterActive && Colors.black) ||
      (isWeb ? Colors.white : Colors.primaryGrayBackground)
    );
  };

  const getTextColor = () => {
    return isFilterActive ? Colors.white : Colors.black;
  };

  const getHoverStyle = () => {
    if (isFilterActive) {
      return isHovered ? styles.hoveredActive : undefined;
    }
    return isHovered ? styles.hovered : undefined;
  };

  const RenderBadge = () => {
    //TODO: in the future we can update this function
    if (hasGroups() && !isColumnsFilter && !isTypesFilter && !isLabelsFilter && !isDateFilter && !isCategoriesFilter)
      return <Badge style={styles.badgeStyle}>{filter.getGroupsCount && filter.getGroupsCount()}</Badge>;
    else if (isAccountTypesFilter && hasAccountTypes())
      return (
        <Badge style={styles.badgeStyle}>
          {filter.getAccountTypesCount && filter.getAccountTypesCount()}
        </Badge>
      );
    else if (isColumnsFilter && hasColumns())
      return (
        <Badge style={styles.badgeStyle}>
          {filter.getColumnsCount && filter.getColumnsCount()}
        </Badge>
      );
    else if (isCategoriesFilter && hasCategories())
      return (
        <Badge style={styles.badgeStyle}>
          {filter.getCategoriesCount && filter.getCategoriesCount()}
        </Badge>
      );
    else if (isTypesFilter && hasTypes())
      return (
        <Badge style={styles.badgeStyle}>
          {filter.getTypesCount && filter.getTypesCount()}
        </Badge>
      );
    else if (isLabelsFilter && hasLabels())
      return (
        <Badge style={styles.badgeStyle}>
          {filter.getLabelsCount && filter.getLabelsCount()}
        </Badge>
      );
    else {
      return;
    }
  };

  return (
    <Pressable
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={[
        { backgroundColor: getBackgroundColor() },
        getHoverStyle(),
        styles.container,
      ]}
      onPress={onGoFilter}
    >
      <Text style={{
        color: getTextColor(),
        ...styles.label
      }}>
        {t(`${label}`)}
      </Text>
      {RenderBadge()}
      <MaterialCommunityIcons
        name={'chevron-down'}
        size={16}
        color={getTextColor()}
      />
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    height: 32,
    borderRadius: 30,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 8,
    marginRight: 8,
  },
  label: {
    fontSize: 16,
    fontWeight: '500',
    letterSpacing: -0.25,
    marginHorizontal: 4,
  },
  badgeStyle: {
    backgroundColor: Colors.white,
    color: Colors.black,
    alignSelf: 'center',
    fontSize: 12,
    fontWeight: '500',
    marginRight: 4,
  },
  hovered: {
    backgroundColor: Colors.grayDivider,
  },
  hoveredActive: {
    backgroundColor: Colors.grayFill,
  },
});
